import { fmtPct, fmtNum } from '../utils/fmt';
import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchWithCache, getFetchMeta } from '../utils/fetchWithCache';
import InfoTooltip from './InfoTooltip';
import SourceDot, { mergeMeta } from './SourceDot';
import { fetchLinearTickers, tickersMeta } from '../utils/bybit';
import DataSourceDown from './DataSourceDown';
import './FundingTab.css';
import { openAlertDraft } from '../utils/alerts';

const TOP_COINS = [
  { sym: 'BTC',  label: 'Bitcoin',       bybit: 'BTCUSDT',  binance: 'BTCUSDT'  },
  { sym: 'ETH',  label: 'Ethereum',      bybit: 'ETHUSDT',  binance: 'ETHUSDT'  },
  { sym: 'SOL',  label: 'Solana',        bybit: 'SOLUSDT',  binance: 'SOLUSDT'  },
  { sym: 'BNB',  label: 'BNB',           bybit: 'BNBUSDT',  binance: 'BNBUSDT'  },
  { sym: 'XRP',  label: 'XRP',           bybit: 'XRPUSDT',  binance: 'XRPUSDT'  },
  { sym: 'DOGE', label: 'Dogecoin',      bybit: 'DOGEUSDT', binance: 'DOGEUSDT' },
  { sym: 'AVAX', label: 'Avalanche',     bybit: 'AVAXUSDT', binance: 'AVAXUSDT' },
  { sym: 'LINK', label: 'Chainlink',     bybit: 'LINKUSDT', binance: 'LINKUSDT' },
  { sym: 'SUI',  label: 'Sui',           bybit: 'SUIUSDT',  binance: 'SUIUSDT'  },
  { sym: 'NEAR', label: 'NEAR Protocol', bybit: 'NEARUSDT', binance: 'NEARUSDT' },
  { sym: 'HYPE', label: 'Hyperliquid',   bybit: 'HYPEUSDT', binance: null       },
  { sym: 'ADA',  label: 'Cardano',       bybit: 'ADAUSDT',  binance: 'ADAUSDT'  },
  { sym: 'DOT',  label: 'Polkadot',      bybit: 'DOTUSDT',  binance: 'DOTUSDT'  },
  // MATIC was renamed to POL (Bybit has no MATICUSDT any more; Binance still
  // answers for MATICUSDT with a frozen, delisted price).
  { sym: 'POL',  label: 'Polygon (POL)', bybit: 'POLUSDT',  binance: 'POLUSDT'  },
  // TON: no Bybit linear perpetual and no Binance USDT-M contract — removed.
];

const REFRESH_MS = 5 * 60 * 1000;

// ── Helpers ────────────────────────────────────────────────────────────────────

function fmtRate(rate) {
  if (rate == null) return '—';
  return fmtPct(rate * 100, 4);
}

function rateColor(rate) {
  if (rate == null) return 'var(--text3)';
  const p = rate * 100;
  if (p > 0.01)   return '#00c853';
  if (p > 0.005)  return '#7ecf7e';
  if (p < -0.01)  return '#ff4d4d';
  if (p < -0.005) return '#cf7e7e';
  return 'var(--text2)';
}

function getSignal(rate) {
  if (rate == null) return { text: '— Nincs adat', cls: 'sig-neutral' };
  const p = rate * 100;
  if (p > 0.01)   return { text: '⚠ Túltőkésített Long',  cls: 'sig-danger'   };
  if (p > 0.005)  return { text: '📈 Long Torzítás',       cls: 'sig-warning'  };
  if (p < -0.01)  return { text: '⚠ Túltőkésített Short', cls: 'sig-positive' };
  if (p < -0.005) return { text: '📉 Short Torzítás',      cls: 'sig-warning'  };
  return                  { text: '⚖ Semleges',            cls: 'sig-neutral'  };
}

function fmtCountdown(ts) {
  if (!ts) return '—';
  const ms = Number(ts) - Date.now();
  if (ms <= 0) return '00:00';
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function aprStr(rate) {
  if (rate == null) return '—';
  return fmtPct(rate * 3 * 365 * 100, 1);
}

// ── SVG Bar Chart ─────────────────────────────────────────────────────────────

function BarChart({ data }) {
  if (!data || data.length === 0) {
    return <div className="fund-chart-empty">Nincs elérhető előzményadat</div>;
  }

  const W = 800, H = 260;
  const PAD = { top: 24, right: 20, bottom: 52, left: 62 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;

  const rates = data.map(d => d.fundingRate);
  const maxAbs = Math.max(...rates.map(Math.abs), 0.00001);
  const yExtent = maxAbs * 1.35;

  const toY = r => PAD.top + cH * (1 - (r + yExtent) / (2 * yExtent));
  const zeroY = toY(0);
  const barW = cW / data.length;

  const yTicks = [-yExtent, -yExtent / 2, 0, yExtent / 2, yExtent];
  const xStep = Math.ceil(data.length / 7);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="fund-chart-svg">
      {yTicks.map((v, i) => (
        <line key={i}
          x1={PAD.left} y1={toY(v)} x2={PAD.left + cW} y2={toY(v)}
          stroke="var(--border2)" strokeWidth="0.5" strokeDasharray="3,4" />
      ))}
      {/* Zero line */}
      <line x1={PAD.left} y1={zeroY} x2={PAD.left + cW} y2={zeroY}
        stroke="var(--text3)" strokeWidth="1.2" />
      {/* Bars */}
      {data.map((d, i) => {
        const x = PAD.left + i * barW + barW * 0.12;
        const w = Math.max(barW * 0.76, 1);
        const y1 = toY(d.fundingRate);
        const isPos = d.fundingRate >= 0;
        const isLatest = i === data.length - 1;
        const fill = isPos
          ? isLatest ? '#00c853' : 'rgba(0,200,83,0.55)'
          : isLatest ? '#ff4d4d' : 'rgba(255,77,77,0.55)';
        const barTop = isPos ? y1 : zeroY;
        const barH = Math.max(Math.abs(y1 - zeroY), 1);
        return (
          <g key={i}>
            <rect x={x} y={barTop} width={w} height={barH} fill={fill} rx={1} />
            {isLatest && (
              <text
                x={x + w / 2}
                y={isPos ? y1 - 5 : zeroY + barH + 11}
                textAnchor="middle" fill="#00e5ff" fontSize="8" fontWeight="bold"
              >MOST</text>
            )}
          </g>
        );
      })}
      {/* Y labels */}
      {yTicks.map((v, i) => (
        <text key={i} x={PAD.left - 6} y={toY(v) + 4}
          textAnchor="end" fill="var(--text2)" fontSize="9">
          {fmtNum(v * 100, 3)}%
        </text>
      ))}
      {/* X labels */}
      {data.map((d, i) => {
        if (i % xStep !== 0 && i !== data.length - 1) return null;
        const x = PAD.left + i * barW + barW / 2;
        const date = new Date(Number(d.fundingRateTimestamp));
        const label = date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
        return (
          <text key={i} x={x} y={H - PAD.bottom + 14}
            textAnchor="middle" fill="var(--text2)" fontSize="9"
            transform={`rotate(-35,${x},${H - PAD.bottom + 14})`}>
            {label}
          </text>
        );
      })}
      {/* Y axis line */}
      <line x1={PAD.left} y1={PAD.top} x2={PAD.left} y2={PAD.top + cH}
        stroke="var(--border)" strokeWidth="1" />
    </svg>
  );
}

// ── Calculator result row ─────────────────────────────────────────────────────

function CalcRow({ label, value }) {
  const isCost   = value != null && value > 0;
  const isIncome = value != null && value < 0;
  return (
    <div className="fund-calc-row">
      <span className="fund-calc-row-label">{label}</span>
      <span className="fund-calc-row-value" style={{
        color: isCost ? 'var(--negative)' : isIncome ? 'var(--positive)' : 'var(--text2)',
      }}>
        {value != null
          ? `${isIncome ? '+' : '−'}${fmtNum(Math.abs(value), 2)} $ ${isIncome ? '(bevétel)' : '(költség)'}`
          : '—'}
      </span>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function FundingTab() {
  const [tableData,      setTableData]      = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [downSources,    setDownSources]    = useState([]);
  const [ratesMeta,      setRatesMeta]      = useState(null);
  const [historyMeta,    setHistoryMeta]    = useState(null);

  const [chartCoin,      setChartCoin]      = useState('BTCUSDT');
  const [historyData,    setHistoryData]    = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [calcSize, setCalcSize] = useState('10000');
  const [calcCoin, setCalcCoin] = useState('BTC');
  const [calcDir,  setCalcDir]  = useState('long');

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  // ── Fetch live rates ───────────────────────────────────────────────────────
  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // The shared, slimmed Bybit ticker list (utils/bybit.js) — the same
      // single request the Derivatívák tab and the Dashboard use.
      const [bbRes, bnRes] = await Promise.allSettled([
        fetchLinearTickers(5 * 60 * 1000),
        fetchWithCache('https://fapi.binance.com/fapi/v1/premiumIndex', { cacheDuration: 5 * 60 * 1000 }),
      ]);

      const bybitMap = {};
      if (bbRes.status === 'fulfilled') {
        for (const t of Object.values(bbRes.value)) {
          bybitMap[t.symbol] = { rate: parseFloat(t.fundingRate), nextFunding: t.nextFundingTime };
        }
      }

      const binanceMap = {};
      if (bnRes.status === 'fulfilled' && Array.isArray(bnRes.value)) {
        for (const t of bnRes.value) {
          binanceMap[t.symbol] = { rate: parseFloat(t.lastFundingRate), nextFunding: t.nextFundingTime };
        }
      }

      const rows = TOP_COINS.map(coin => {
        const bb = bybitMap[coin.bybit] ?? null;
        const bn = coin.binance ? (binanceMap[coin.binance] ?? null) : null;
        const bybitRate   = bb ? bb.rate : null;
        const binanceRate = bn ? bn.rate : null;
        const avgRate = bybitRate != null && binanceRate != null
          ? (bybitRate + binanceRate) / 2
          : (bybitRate ?? binanceRate);
        return { ...coin, bybitRate, binanceRate, avgRate, nextFunding: bb?.nextFunding ?? bn?.nextFunding ?? null };
      });

      const meta = mergeMeta(
        tickersMeta(),
        getFetchMeta('https://fapi.binance.com/fapi/v1/premiumIndex'),
      );
      const down = [bbRes.status !== 'fulfilled' && 'Bybit', bnRes.status !== 'fulfilled' && 'Binance'].filter(Boolean);
      if (mounted.current) { setTableData(rows); setRatesMeta(meta); setDownSources(down); setError(null); }
    } catch (e) {
      if (mounted.current) setError(e.message);
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  // ── Fetch history ──────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async (symbol) => {
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const url = `https://api.bybit.com/v5/market/funding/history?category=linear&symbol=${symbol}&limit=30`;
      const json = await fetchWithCache(url, { cacheDuration: 5 * 60 * 1000 });
      if (mounted.current) setHistoryMeta(getFetchMeta(url));
      const list = (json?.result?.list ?? [])
        .map(d => ({ fundingRate: parseFloat(d.fundingRate), fundingRateTimestamp: d.fundingRateTimestamp }))
        .reverse();
      if (mounted.current) setHistoryData(list);
    } catch (_) {
      if (mounted.current) setHistoryData([]);
    } finally {
      if (mounted.current) setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { fetchRates(); },          [fetchRates]);
  useEffect(() => { fetchHistory(chartCoin); }, [chartCoin, fetchHistory]);

  // Auto-refresh
  useEffect(() => {
    const id = setInterval(() => { fetchRates(); fetchHistory(chartCoin); }, REFRESH_MS);
    return () => clearInterval(id);
  }, [fetchRates, fetchHistory, chartCoin]);

  // Countdown re-render ticker (every 30s)
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // ── Calculator ─────────────────────────────────────────────────────────────
  const calcRow  = tableData.find(r => r.sym === calcCoin);
  const calcRate = calcRow?.avgRate ?? null;
  const posSize  = Math.max(parseFloat(calcSize) || 0, 0);

  // positive return = user pays; negative = user earns
  function payment(rate, dir) {
    if (rate == null || posSize <= 0) return null;
    return rate * posSize * (dir === 'long' ? 1 : -1);
  }

  const pay8h    = payment(calcRate, calcDir);
  const payDay   = pay8h   != null ? pay8h   * 3  : null;
  const payWeek  = payDay  != null ? payDay  * 7  : null;
  const payMonth = payDay  != null ? payDay  * 30 : null;
  const breakEven8h = pay8h != null && posSize > 0 ? (Math.abs(pay8h) / posSize) * 100 : null;

  // ── Extreme alerts ─────────────────────────────────────────────────────────
  const extremes = tableData.filter(r => r.avgRate != null && Math.abs(r.avgRate * 100) > 0.01);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="fund-root">

      {/* ── Header ── */}
      <div className="fund-header">
        <span className="fund-title">⚡ Funding Ráták</span>
        <button className="fund-refresh-btn" onClick={fetchRates} disabled={loading}>
          {loading ? '◈' : '↻'}
        </button>
      </div>

      {error && <div className="fund-error">⚠ {error}</div>}
      {downSources.map(src => <DataSourceDown key={src} source={src} onRetry={fetchRates} />)}

      {/* ── Section 1: Table ── */}
      <section className="fund-section">
        <div className="fund-section-title">Funding Ráta Táblázat · {TOP_COINS.length} coin<SourceDot source="Bybit · Binance" note="Automatikus frissítés 5 percenként." meta={ratesMeta} expectedMs={5 * 60 * 1000} /></div>
        <div className="fund-table-wrap">
          <table className="fund-table">
            <thead>
              <tr>
                <th>Coin</th>
                <th className="right">Bybit Ráta</th>
                <th className="right">Binance Ráta</th>
                <th className="right">Átlag Ráta</th>
                <th className="right">8h / $10k</th>
                <th className="right">APR</th>
                <th>Szignál</th>
                <th className="right">Következő Funding</th>
              </tr>
            </thead>
            <tbody>
              {tableData.map(row => {
                const sig = getSignal(row.avgRate);
                const pay = row.avgRate != null ? Math.abs(row.avgRate) * 10000 : null;
                return (
                  <tr key={row.sym}>
                    <td className="fund-coin-cell">
                      <span className="fund-sym">{row.sym}</span>
                      <span className="fund-label">{row.label}</span>
                      {TOP_COINS.find(c => c.sym === row.sym)?.bybit && (
                        <button type="button" className="alert-row-btn" title="Funding-riasztás"
                          aria-label={`Funding-riasztás: ${row.sym}`}
                          onClick={() => openAlertDraft({ kind: 'funding', symbol: TOP_COINS.find(c => c.sym === row.sym).bybit, source: 'funding' })}>🔔</button>
                      )}
                    </td>
                    <td className="right" style={{ color: rateColor(row.bybitRate) }}>
                      {fmtRate(row.bybitRate)}
                    </td>
                    <td className="right" style={{ color: rateColor(row.binanceRate) }}>
                      {fmtRate(row.binanceRate)}
                    </td>
                    <td className="right fund-avg" style={{ color: rateColor(row.avgRate) }}>
                      {fmtRate(row.avgRate)}
                    </td>
                    <td className="right">
                      {pay != null
                        ? <span style={{ color: row.avgRate >= 0 ? 'var(--negative)' : 'var(--positive)' }}>
                            {fmtNum(pay, 2)} $
                          </span>
                        : '—'}
                    </td>
                    <td className="right" style={{ color: rateColor(row.avgRate) }}>
                      {aprStr(row.avgRate)}
                    </td>
                    <td>
                      <span className={`fund-badge ${sig.cls}`}>{sig.text}</span>
                    </td>
                    <td className="right fund-countdown">
                      {fmtCountdown(row.nextFunding)}
                    </td>
                  </tr>
                );
              })}
              {tableData.length === 0 && !loading && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--text3)', padding: '24px' }}>
                    {error ? 'Az adatok betöltése nem sikerült.' : 'Betöltés…'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="fund-table-note">
          Pozitív ráta → longok fizetnek shortoknak 8 óránként · Negatív ráta → shortok fizetnek longoknak 8 óránként
        </div>
      </section>

      {/* ── Section 2: History Chart ── */}
      <section className="fund-section">
        <div className="fund-section-title">
          Funding Ráta Előzmények (utolsó 30 periódus)
          <SourceDot source="Bybit (funding előzmények)" meta={historyMeta} />
          <select
            className="fund-select"
            value={chartCoin}
            onChange={e => setChartCoin(e.target.value)}
          >
            {TOP_COINS.map(c => (
              <option key={c.bybit} value={c.bybit}>{c.sym} — {c.label}</option>
            ))}
          </select>
        </div>
        <div className="fund-chart-container">
          {historyLoading
            ? <div className="fund-chart-empty">Előzmények betöltése…</div>
            : <BarChart data={historyData} />}
        </div>
      </section>

      {/* ── Section 3: Calculator ── */}
      <section className="fund-section">
        <div className="fund-section-title">Funding Kalkulátor</div>
        <div className="fund-calc-inputs">
          <label className="fund-calc-label">
            Pozíció Méret (USD)
            <input
              type="number"
              className="fund-calc-input"
              value={calcSize}
              min="0"
              onChange={e => setCalcSize(e.target.value)}
              placeholder="10000"
            />
          </label>
          <label className="fund-calc-label">
            Coin
            <select
              className="fund-select"
              value={calcCoin}
              onChange={e => setCalcCoin(e.target.value)}
            >
              {TOP_COINS.map(c => (
                <option key={c.sym} value={c.sym}>{c.sym}</option>
              ))}
            </select>
          </label>
          <label className="fund-calc-label">
            Irány
            <div className="fund-dir-btns">
              <button
                className={`fund-dir-btn ${calcDir === 'long' ? 'active-long' : ''}`}
                onClick={() => setCalcDir('long')}
              >▲ Long</button>
              <button
                className={`fund-dir-btn ${calcDir === 'short' ? 'active-short' : ''}`}
                onClick={() => setCalcDir('short')}
              >▼ Short</button>
            </div>
          </label>
        </div>

        {calcRate != null ? (
          <div className="fund-calc-results">
            <div className="fund-calc-rate-row">
              <span className="fund-calc-rate-label">Jelenlegi átlag ráta ({calcCoin})</span>
              <span style={{ color: rateColor(calcRate), fontSize: '17px', fontWeight: 700 }}>
                {fmtRate(calcRate)}
              </span>
              <span style={{ color: 'var(--text3)', fontSize: '11px' }}>
                APR {aprStr(calcRate)}
              </span>
            </div>
            <div className="fund-calc-grid">
              <CalcRow label="8h fizetés"  value={pay8h}   />
              <CalcRow label="Naponta (×3)" value={payDay}  />
              <CalcRow label="Hetente"       value={payWeek} />
              <CalcRow label="Havonta"       value={payMonth}/>
              {breakEven8h != null && (
                <div className="fund-calc-row fund-calc-breakeven">
                  <span className="fund-calc-row-label">Nullszaldós árelmozgás (8 óra)</span>
                  <span className="fund-calc-row-value" style={{ color: 'var(--accent)' }}>
                    {fmtNum(breakEven8h, 4)}% árelmozgás szükséges a funding fedezéséhez
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="fund-calc-empty">Nincs elérhető ráta adat: {calcCoin}</div>
        )}
      </section>

      {/* ── Section 4: Extreme Alerts ── */}
      {extremes.length > 0 && (
        <section className="fund-section">
          <div className="fund-section-title">⚠ Extrém Funding Riasztások <InfoTooltip text="A funding ráta az, amit a longosok fizetnek a shortosoknak (vagy fordítva) 8 óránként. Ha nagyon magas, longot nyitni és zsebelni a fundingot lehetséges stratégia — de ha az ár közben esik, a pozíció veszteséges lehet. Ez lehetőséget mutat, nem garanciát." /></div>
          <div className="fund-alerts-grid">
            {extremes.map(row => {
              const isPos = (row.avgRate ?? 0) > 0;
              return (
                <div key={row.sym} className={`fund-alert-card ${isPos ? 'alert-long' : 'alert-short'}`}>
                  <div className="fund-alert-top">
                    <span className="fund-alert-sym">{row.sym}</span>
                    <span className="fund-alert-rate" style={{ color: rateColor(row.avgRate) }}>
                      {fmtRate(row.avgRate)}
                    </span>
                    <span className={`fund-badge ${getSignal(row.avgRate).cls}`}>
                      {getSignal(row.avgRate).text}
                    </span>
                  </div>
                  <div className="fund-alert-meaning">
                    {isPos
                      ? `A longosok shortokat fizetnek ${fmtRate(row.avgRate)}-t minden 8 órában — a piac erősen long-torzított.`
                      : `A shortosok longokat fizetnek ${fmtRate(Math.abs(row.avgRate ?? 0))}-t minden 8 órában — a piac erősen short-torzított.`}
                  </div>
                  <div className="fund-alert-opportunity">
                    {isPos
                      ? '💡 Lehetőség: nyiss shortot a funding bevételhez, vagy kerüld a long pozíciót itt.'
                      : '💡 Lehetőség: nyiss longot a funding bevételhez, vagy kerüld a short pozíciót itt.'}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

    </div>
  );
}
